import { Box, ModalHeader, Typo, useModal } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useHref, useNavigate } from 'react-router'

import { with410ErrorHandling } from '../../../../../clients/api'
import { PolicyAdjustmentResponse } from '../../../../../domain'
import { useCoverLevelOption } from '../../../../../hooks/coverOption'
import { useErrorHandlingAsyncCallback } from '../../../../../hooks/errors'
import { useStripeMtaAdmin, useStripeMtaUser } from '../../../../../hooks/stripeMta'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../../authentication'
import { ActionBar } from '../../../../organisms/form'
import { useProduct } from '../../../ProductProvider'
import { usePolicy } from '../../PolicyProvider'
import { PolicyAdjustmentResultPath } from './PolicyAdjustmentResultPath'

interface AdjustmentActionBarProps {
  adjustment: PolicyAdjustmentResponse
  onRepeat: () => void
}

const tKey = TKey('pages.adjustment-action-bar')

export const AdjustmentActionBar: FunctionComponent<AdjustmentActionBarProps> = ({
  adjustment,
  onRepeat,
}) => {
  const { isAdmin } = usePrincipal()

  const navigate = useNavigate()

  const modal = useModal()
  const { t } = useWriskTranslation()
  const { apiClient } = usePrincipal()
  const {
    setPolicy,
    refresh,
    policy: {
      currentPolicyVersion: { quote },
    },
  } = usePolicy()
  const { product } = useProduct()

  const { coverLevelOption } = useCoverLevelOption(quote, product)

  const { adjustmentId, policyId, amountDue } = adjustment

  const onStripeMtaUser = useStripeMtaUser({
    adjustmentId,
    successPath: useHref(PolicyAdjustmentResultPath.RESULT),
    cancelPath: useHref('../..'),
  })

  const onStripeMtaAdmin = useStripeMtaAdmin({
    adjustmentId,
    onSuccess: async () => {
      await refresh()
      navigate(PolicyAdjustmentResultPath.RESULT)
    },
  })

  const requiresPayment =
    amountDue > 0 && coverLevelOption?.paymentScheduleType === 'SINGLE_PAYMENT'

  const onConfirm = useErrorHandlingAsyncCallback(() =>
    with410ErrorHandling(
      async () => {
        if (requiresPayment) {
          isAdmin ? await onStripeMtaAdmin.execute() : await onStripeMtaUser.execute()
        } else {
          await apiClient.confirmPolicyAdjustment(adjustmentId)
          setPolicy(await apiClient.getPolicy(policyId))
          navigate(PolicyAdjustmentResultPath.RESULT)
        }
      },
      () =>
        modal.show({
          content: (
            <Box>
              <ModalHeader header={t(tKey('error-gone', 'header'))} />
              <Typo>{t(tKey('error-gone', 'content'))}</Typo>
            </Box>
          ),
        }),
    ),
  )

  const actionTKey = requiresPayment ? TExtends(tKey, 'debit') : tKey

  return (
    <ActionBar
      tKey={actionTKey}
      onConfirm={onConfirm.execute}
      onDismiss={onRepeat}
      loading={onConfirm.loading}
    />
  )
}
